import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Rocket, Loader2, Settings, Minus, Plus, CalendarClock, X, ChevronLeft } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import knowledgeGapsService from '@/lib/api/knowledgeGaps';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

// ─── Constants ──────────────────────────────────────────────────────────────

const MIN_TRACES = 1;
const MAX_TRACES = 1000;
const DEFAULT_TRACES = 200;

type PipelineSection = 'run-analysis' | 'consolidation';

const PIPELINE_SECTIONS: {
  key: PipelineSection;
  icon: React.FC<{ className?: string }>;
  label: string;
  description: string;
}[] = [
  {
    key: 'run-analysis',
    icon: Rocket,
    label: 'Run Analysis',
    description: 'Fetch traces, extract gaps, and consolidate into summaries.',
  },
  {
    key: 'consolidation',
    icon: Settings,
    label: 'Consolidation',
    description: 'Re-consolidate existing findings without re-analyzing.',
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function clampTraces(value: number): number {
  if (Number.isNaN(value)) return DEFAULT_TRACES;
  return Math.max(MIN_TRACES, Math.min(MAX_TRACES, Math.round(value)));
}

// ─── Component ──────────────────────────────────────────────────────────────

interface TriggerPipelineTabProps {
  orgId: string;
}

export function TriggerPipelineTab({ orgId }: TriggerPipelineTabProps) {
  const isMobile = useIsMobile();
  const [activeSection, setActiveSection] = useState<PipelineSection>('run-analysis');
  const [mobileSectionView, setMobileSectionView] = useState<'list' | 'detail'>('list');
  const queryClient = useQueryClient();

  // Pipeline form state
  const [maxTraces, setMaxTraces] = useState(DEFAULT_TRACES);
  const [skipConsolidation, setSkipConsolidation] = useState(false);

  // Consolidation form state
  const [sinceDate, setSinceDate] = useState<Date | undefined>(undefined);
  const [sincePopoverOpen, setSincePopoverOpen] = useState(false);
  const [reprocessAll, setReprocessAll] = useState(false);

  // ── Styling (matching SalesAgentPage) ─────────────────────────────────

  const sectionNavBase =
    'w-full rounded-lg border border-transparent px-3 py-3 text-left';
  const sectionNavActive = 'border-border bg-background text-foreground shadow-sm';
  const sectionButtonClass = (section: PipelineSection) =>
    cn(sectionNavBase, activeSection === section && sectionNavActive);

  const currentSectionMeta = PIPELINE_SECTIONS.find((s) => s.key === activeSection)!;

  // ── Navigation ────────────────────────────────────────────────────────

  const selectSection = (section: PipelineSection) => {
    setActiveSection(section);
    if (isMobile) {
      setMobileSectionView('detail');
    }
  };

  // ── Mutations ───────────────────────────────────────────────────────────

  const triggerRunMutation = useMutation({
    mutationFn: async () => {
      const res = await knowledgeGapsService.triggerRun(orgId, {
        max_traces: clampTraces(maxTraces),
        skip_consolidation: skipConsolidation,
      });
      if (!res.success || !res.data) {
        const errorMsg = res.error || 'Failed to trigger pipeline';
        if (errorMsg.toLowerCase().includes('already') || errorMsg.toLowerCase().includes('in progress')) {
          throw new Error('CONFLICT: ' + errorMsg);
        }
        throw new Error(errorMsg);
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-runs', orgId] });
      toast({
        title: 'Pipeline started',
        description: `Run ${data.run_id.slice(0, 8)}... is now processing.`,
      });
    },
    onError: (error: Error) => {
      const isConflict = error.message.startsWith('CONFLICT:');
      toast({
        title: isConflict ? 'Run already in progress' : 'Failed to start pipeline',
        description: isConflict
          ? 'Another analysis run is already in progress. Please wait for it to complete.'
          : error.message,
        variant: isConflict ? undefined : 'destructive',
      });
    },
  });

  const consolidateMutation = useMutation({
    mutationFn: async () => {
      const payload: { since?: string; reprocess_all: boolean } = {
        reprocess_all: reprocessAll,
      };
      if (sinceDate) {
        payload.since = sinceDate.toISOString();
      }
      const res = await knowledgeGapsService.triggerConsolidation(orgId, payload);
      if (!res.success || !res.data) {
        const errorMsg = res.error || 'Failed to trigger consolidation';
        if (errorMsg.toLowerCase().includes('already') || errorMsg.toLowerCase().includes('in progress')) {
          throw new Error('CONFLICT: ' + errorMsg);
        }
        throw new Error(errorMsg);
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-runs', orgId] });
      toast({
        title: 'Consolidation started',
        description: `Run ${data.run_id.slice(0, 8)}... is now consolidating.`,
      });
    },
    onError: (error: Error) => {
      const isConflict = error.message.startsWith('CONFLICT:');
      toast({
        title: isConflict ? 'Run already in progress' : 'Consolidation failed',
        description: isConflict
          ? 'Another run is already in progress. Please wait for it to complete.'
          : error.message,
        variant: isConflict ? undefined : 'destructive',
      });
    },
  });

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleMaxTracesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setMaxTraces(0 as any);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      setMaxTraces(parsed);
    }
  };

  const handleMaxTracesBlur = () => {
    setMaxTraces(clampTraces(maxTraces));
  };

  const incrementTraces = () => {
    setMaxTraces((prev) => clampTraces(prev + 10));
  };

  const decrementTraces = () => {
    setMaxTraces((prev) => clampTraces(prev - 10));
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-5 xl:grid xl:grid-cols-[280px_minmax(0,1fr)]">
      {/* Sidebar */}
      {(!isMobile || mobileSectionView === 'list') && (
        <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:self-start">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {/* Header */}
            <div className="border-b border-border px-4 py-4">
              <h2 className="text-base font-semibold tracking-tight">Pipeline</h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Run analysis or re-consolidate existing findings.
              </p>
            </div>

            {/* Navigation Items */}
            <div className="p-3">
              <nav className="flex flex-col gap-2">
                {PIPELINE_SECTIONS.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    className={sectionButtonClass(section.key)}
                    onClick={() => selectSection(section.key)}
                  >
                    <div className="flex items-start gap-3">
                      <section.icon
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          activeSection === section.key
                            ? 'text-foreground'
                            : 'text-muted-foreground',
                        )}
                      />
                      <div className="min-w-0">
                        <p
                          className={cn(
                            'text-sm font-medium',
                            activeSection === section.key
                              ? 'text-foreground'
                              : 'text-muted-foreground',
                          )}
                        >
                          {section.label}
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          {section.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </aside>
      )}

      {/* Main Content */}
      {(!isMobile || mobileSectionView === 'detail') && (
        <main className="min-w-0 overflow-hidden rounded-xl border border-border bg-background shadow-sm">
          {/* Mobile back button */}
          {isMobile && (
            <div className="border-b border-border px-4 py-3 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileSectionView('list')}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <span className="text-sm text-muted-foreground">Pipeline</span>
            </div>
          )}

          {/* Content header */}
          <div className="border-b border-border px-6 py-5 lg:px-8">
            <h2 className="text-2xl font-semibold tracking-tight">
              {currentSectionMeta.label}
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {currentSectionMeta.description}
            </p>
          </div>

          {/* Section content */}
          <div className="px-6 py-8 lg:px-8 space-y-6">
            {activeSection === 'run-analysis' && (
              <>
                <Alert>
                  <AlertDescription>
                    This will fetch the latest conversation traces, extract
                    unanswered questions and knowledge gaps, and consolidate
                    them into actionable summaries.
                  </AlertDescription>
                </Alert>

                <div className="space-y-5">
                  {/* Max traces input */}
                  <div className="space-y-1.5">
                    <Label htmlFor="max-traces">Max Traces to Analyze</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="max-traces"
                        type="number"
                        min={MIN_TRACES}
                        max={MAX_TRACES}
                        value={maxTraces || ''}
                        onChange={handleMaxTracesChange}
                        onBlur={handleMaxTracesBlur}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={decrementTraces}
                        disabled={maxTraces <= MIN_TRACES}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={incrementTraces}
                        disabled={maxTraces >= MAX_TRACES}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Skip consolidation checkbox */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="skip-consolidation"
                      checked={skipConsolidation}
                      onCheckedChange={(checked) =>
                        setSkipConsolidation(checked === true)
                      }
                    />
                    <Label htmlFor="skip-consolidation" className="cursor-pointer">
                      Skip Consolidation (Only extract raw findings)
                    </Label>
                  </div>

                  {/* Start pipeline button */}
                  <Button
                    className="w-full"
                    disabled={triggerRunMutation.isPending}
                    onClick={() => triggerRunMutation.mutate()}
                  >
                    {triggerRunMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Rocket className="h-4 w-4 mr-2" />
                    )}
                    Start Pipeline
                  </Button>
                </div>
              </>
            )}

            {activeSection === 'consolidation' && (
              <div className="space-y-5">
                {/* Since date picker */}
                <div className="space-y-1.5">
                  <Label>Since (optional)</Label>
                  <div className="flex items-center gap-2">
                    <Popover open={sincePopoverOpen} onOpenChange={setSincePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !sinceDate && 'text-muted-foreground',
                          )}
                        >
                          <CalendarClock className="h-4 w-4 mr-2" />
                          {sinceDate
                            ? format(sinceDate, 'MMM d, yyyy')
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={sinceDate}
                          onSelect={(date) => {
                            setSinceDate(date);
                            setSincePopoverOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {sinceDate && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSinceDate(undefined)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Only consolidate findings created after this date.
                  </p>
                </div>

                {/* Reprocess all checkbox */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="reprocess-all"
                    checked={reprocessAll}
                    onCheckedChange={(checked) =>
                      setReprocessAll(checked === true)
                    }
                  />
                  <Label htmlFor="reprocess-all" className="cursor-pointer">
                    Reprocess All (Reset all findings and re-consolidate from
                    scratch)
                  </Label>
                </div>

                {/* Consolidate button */}
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={consolidateMutation.isPending}
                  onClick={() => consolidateMutation.mutate()}
                >
                  {consolidateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="h-4 w-4 mr-2" />
                  )}
                  Run Consolidation
                </Button>
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  );
}
